import { FC } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Center,
  Breadcrumb as ChakraBreadcrumb,
  Flex,
  Icon,
  List,
  ListItem,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { BiChevronRight } from 'react-icons/bi';

import {
  Breadcrumb,
  FileMoveDirectoryFragment,
  useGetFileMoveDirectoryQuery,
} from '../../../../graphql/generated';
import { FolderIcon } from '../../../../ui/icons/folder';
import { BreadcrumbItem } from './breadcrumb-item';
import { FileMoveProps } from './file-move-action';
import { takeDirectories } from './utils/object-utils';

type FileMoveBrowserProps = {
  file: FileMoveProps;
  selectDirectory: (directory: FileMoveDirectoryFragment | null) => void;
};

export const FileMoveBrowser: FC<FileMoveBrowserProps> = ({
  file,
  selectDirectory,
}) => {
  const { data, loading, error, refetch } = useGetFileMoveDirectoryQuery({
    variables: {
      cedant: file.cedant,
      directoryId: file.parentDirectoryId,
    },
    fetchPolicy: 'cache-and-network',
  });

  if (loading) {
    return (
      <Center height={22}>
        <Spinner />
      </Center>
    );
  }

  if (error || !data?.cedant) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Could not load directories.</AlertTitle>
        <AlertDescription>Please try again.</AlertDescription>
      </Alert>
    );
  }

  const directories = takeDirectories(data.cedant.directory.objects.objects);

  const handleTreatyBreadcrumbClick = () => {
    refetch({
      cedant: file.cedant,
      directoryId: null,
    });
    selectDirectory(null); // Not allowed to move file to treaty dir
  };

  const handleBreadcrumbClick = async (breadcrumb: Breadcrumb) => {
    const { data } = await refetch({
      cedant: file.cedant,
      directoryId: breadcrumb.directoryId,
    });

    const directory = data.cedant?.directory || null;
    selectDirectory(directory);
  };

  const handleDirectoryClick = (directory: FileMoveDirectoryFragment) => {
    refetch({
      cedant: file.cedant,
      directoryId: directory.id,
    });
    selectDirectory(directory);
  };

  const breadcrumbs = data.cedant.directory.breadcrumbs;

  return (
    <Stack>
      <ChakraBreadcrumb
        as="div"
        color="gray.500"
        data-testid="directory-breadcrumbs"
      >
        {breadcrumbs.length > 0 &&
          breadcrumbs.map((breadcrumb, i) => {
            const isFirst = i === 0;
            const uniqueKey = breadcrumb.directoryId || `root-${breadcrumb.name}`;
            
            if (isFirst) {
              return (
                <BreadcrumbItem
                  key={uniqueKey}
                  isActive={breadcrumbs.length === 0}
                  onClick={handleTreatyBreadcrumbClick}
                  value={breadcrumb.name}
                />
              );
            }

            const isLast = i === breadcrumbs.length - 1;
            return (
              <BreadcrumbItem
                key={uniqueKey}
                isActive={isLast}
                onClick={() => handleBreadcrumbClick(breadcrumb)}
                value={breadcrumb.name}
              />
            );
          })}
      </ChakraBreadcrumb>

      <List
        py={2}
        data-testid="directory-list"
        overflowY="auto"
        maxHeight={500} // needed for showing vertical scrollbar
      >
        {directories.map((directory) => (
          <ListItem
            key={directory.id}
            p={4}
            onClick={() => handleDirectoryClick(directory)}
            borderBottomWidth="1px"
            cursor="pointer"
            _hover={{ backgroundColor: 'gray.100' }}
            data-testid="directory-list-item"
          >
            <Flex align="center" justify="space-between">
              <Flex align="center">
                <FolderIcon width="12" height="6" />
                <Text ml={2} color="gray.700" noOfLines={1}>
                  {directory.name}
                </Text>
              </Flex>
              <Icon as={BiChevronRight} color="gray.700" fontSize="2xl" />
            </Flex>
          </ListItem>
        ))}
      </List>
    </Stack>
  );
};
